import { CommonModule } from '@angular/common';
import {
  Component,
  ChangeDetectionStrategy,
  Input,
  OnInit,
  ChangeDetectorRef,
  OnDestroy,
} from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { CULTURE_ROUTE_PARAM, CultureKeyType } from '@constants/app.constants';
import { LetDirective } from '@directives/app-let.directive';
import { ErrorsCopy } from '@models/cms/errors.model';
import { Store } from '@ngrx/store';
import { InterpolatePipe } from '@pipes/interpolate/interpolate.pipe';
import { selectRouteParam } from '@selectors/router.selector';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-input-errors',
  standalone: true,
  imports: [CommonModule, LetDirective, InterpolatePipe],
  templateUrl: './input-errors.component.html',
  styleUrls: ['./input-errors.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InputErrorsComponent implements OnInit, OnDestroy {
  onDestroy$ = new Subject<void>();

  culture$ = this.store.select(
    selectRouteParam(CULTURE_ROUTE_PARAM),
  ) as Observable<CultureKeyType | undefined>;

  @Input()
  errors: ErrorsCopy;

  ngOnInit(): void {
    this.control.statusChanges
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(() => {
        this.changeDetector.detectChanges();
      });
  }

  parentControls: AbstractControl[] = [];

  @Input() control: AbstractControl;
  @Input() extraErrors?: { [key: string]: string };
  @Input() suppressErrors?: string[];

  get doShowAnyErrors(): boolean {
    return (
      this.control.status !== 'VALID' &&
      (this.control.dirty || this.control.touched)
    );
  }

  /**
   *
   * @param key The key to access the error data on the control and in the CMS data
   * @param errorProp The property within the error that gives us a specific value related to the error
   * @returns
   * Whether or not the error is active, based on whether the error key is in the control errors, and
   * optionally whether the error data includes `errorProp`. Will always return false if `key` is
   * in `suppressErrors`
   */
  hasError(key: string, errorProp?: string): boolean {
    if (!this.control.errors) {
      return false;
    }
    return (
      this.control.hasError(key) &&
      (errorProp === undefined ||
        (errorProp !== undefined && errorProp in this.control.errors[key])) &&
      !this.suppressErrors?.includes(key)
    );
  }

  isType(value: unknown, type: string): boolean {
    return typeof value === type;
  }

  isString(value: unknown): value is string {
    return this.isType(value, 'string');
  }

  isObject(value: unknown): value is { [key: string]: string } {
    return this.isType(value, 'object') && value !== null;
  }

  constructor(
    private changeDetector: ChangeDetectorRef,
    private store: Store,
  ) {}

  ngOnDestroy(): void {
    this.onDestroy$.next();
  }
}
